import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TutorAiService, TutorSuggestedQuestion, TutorMessage } from '../../../services/tutor-ai.service';
import { TutorContext } from '../../../services/tutor-context-state.service';
import { TranslateService } from '../../../services/translate.service';

@Component({
    selector: 'app-tutor-assistant',
    templateUrl: './tutor-assistant.component.html',
    styleUrls: ['./tutor-assistant.component.scss'],
})
export class TutorAssistantComponent implements OnInit, OnChanges, OnDestroy {
    @Input() context!: TutorContext;
    @Input() messages: TutorMessage[] = [];
    @Output() messageAdded = new EventEmitter<TutorMessage>();

    @ViewChild('contentContainer') contentContainer!: ElementRef;

    suggestedQuestions: TutorSuggestedQuestion[] = [];
    isLoading = false;
    isLoadingSuggestions = false;

    private destroy$ = new Subject<void>();
    private welcomeSent = false;
    private lastContextKey = '';
    private lastSectionId: string | number | undefined = undefined;

    constructor(
        private tutorAiService: TutorAiService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        if (!this.welcomeSent && this.messages.length === 0 && this.context) {
            this.emitWelcome();
            this.welcomeSent = true;
        }
        if (this.context) {
            this.lastContextKey = this.getContextKey();
            this.lastSectionId = this.context.sectionId;
            this.loadSuggestions();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['context'] && !changes['context'].firstChange && this.context) {
            const newKey = this.getContextKey();
            if (newKey !== this.lastContextKey) {
                const sectionChanged = this.context.sectionId !== this.lastSectionId;
                this.lastContextKey = newKey;
                this.lastSectionId = this.context.sectionId;
                if (this.context.source === 'player') {
                    if (sectionChanged && this.context.sectionTitle) {
                        this.emitSectionChange();
                    } else if (!sectionChanged && this.context.activityTitle) {
                        this.emitActivityChange();
                    }
                }
                this.loadSuggestions();
            }
        }

        if (changes['messages'] && !changes['messages'].firstChange) {
            const msgs: TutorMessage[] = changes['messages'].currentValue;
            if (msgs.length === 0) {
                this.welcomeSent = false;
                this.suggestedQuestions = [];
                this.emitWelcome();
                this.welcomeSent = true;
                this.loadSuggestions();
            }
        }
    }

    onQuestionSelected(question: TutorSuggestedQuestion): void {
        if (this.isLoading) return;

        // Capturar historial antes del emit para evitar que el mensaje
        // del usuario quede duplicado en la conversación enviada al backend
        const historySnapshot = [...this.messages];

        const userMsg: TutorMessage = {
            id: this.generateId(),
            role: 'user',
            content: question.text,
            timestamp: Date.now(),
            type: 'text',
        };
        this.messageAdded.emit(userMsg);

        this.isLoading = true;
        this.suggestedQuestions = [];

        this.tutorAiService
            .askQuestion(question.text, this.context, historySnapshot)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: result => {
                    const assistantMsg: TutorMessage = {
                        id: this.generateId(),
                        role: 'assistant',
                        content: result.answer,
                        timestamp: Date.now(),
                        type: 'text',
                        suggestedQuestions: result.followUpQuestions,
                    };
                    this.messageAdded.emit(assistantMsg);
                    this.suggestedQuestions = result.followUpQuestions;
                    this.isLoading = false;
                    setTimeout(() => this.scrollToBottom(), 100);
                },
                error: () => {
                    // Antes solo se apagaba el spinner: el usuario preguntaba, no pasaba nada y no
                    // había forma de saber que el backend había respondido con un error.
                    this.messageAdded.emit({
                        id: this.generateId(),
                        role: 'assistant',
                        content: this.translateService.instant('chat.tutor.error'),
                        timestamp: Date.now(),
                        type: 'text',
                    });
                    this.isLoading = false;
                    this.suggestedQuestions = this.tutorAiService.getStaticQuestions();
                    setTimeout(() => this.scrollToBottom(), 100);
                },
            });
    }

    private emitWelcome(): void {
        const title = this.context?.contentTitle || '';
        const welcome: TutorMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: this.translateService.instant('chat.tutor.welcome', { title }),
            timestamp: Date.now(),
            type: 'text',
        };
        this.messageAdded.emit(welcome);
    }

    private emitSectionChange(): void {
        const notice: TutorMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: this.translateService.instant('chat.tutor.section-change', { sectionTitle: this.context.sectionTitle }),
            timestamp: Date.now(),
            type: 'text',
        };
        this.messageAdded.emit(notice);
    }

    private emitActivityChange(): void {
        // La clave de traducción es texto plano (sin variables ni HTML): el título y el formato
        // se arman acá.
        const label = this.translateService.instant('chat.tutor.activity-change');
        const notice: TutorMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: `${label} <strong>${this.context.activityTitle}</strong>`,
            timestamp: Date.now(),
            type: 'text',
        };
        this.messageAdded.emit(notice);
    }

    private loadSuggestions(): void {
        this.isLoadingSuggestions = true;
        this.tutorAiService
            .getSuggestions(this.context)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: questions => {
                    this.suggestedQuestions = questions;
                    this.isLoadingSuggestions = false;
                },
                error: () => {
                    this.suggestedQuestions = this.tutorAiService.getStaticQuestions();
                    this.isLoadingSuggestions = false;
                },
            });
    }

    private getContextKey(): string {
        if (!this.context) return '';
        return `${this.context.contentType}-${this.context.contentId}-${this.context.sectionId || ''}-${this.context.activityId || ''}`;
    }

    private scrollToBottom(): void {
        if (this.contentContainer?.nativeElement) {
            const el = this.contentContainer.nativeElement;
            el.scrollTop = el.scrollHeight;
        }
    }

    private generateId(): string {
        return `tutor-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
